import {
  TAX_SLABS_NEW_REGIME_2025,
  STANDARD_DEDUCTION,
  SECTION_44ADA_PRESUMPTIVE_RATE,
  ADVANCE_TAX_DUE_DATES
} from './constants';

/**
 * Calculate tax under New Regime for FY 2025-26
 * @param {number} taxableIncome - Total taxable income
 * @returns {object} Tax breakdown
 */
export function calculateTaxNewRegime(taxableIncome) {
  let remainingIncome = taxableIncome;
  let totalTax = 0;
  const slabBreakdown = [];

  for (const slab of TAX_SLABS_NEW_REGIME_2025) {
    if (remainingIncome <= 0) break;

    const slabRange = slab.max === Infinity ? Infinity : slab.max - slab.min;
    const taxableInSlab = Math.min(remainingIncome, slabRange);
    const taxForSlab = (taxableInSlab * slab.rate) / 100;

    if (taxableInSlab > 0) {
      slabBreakdown.push({
        range: `₹${(slab.min / 100000).toFixed(1)}L - ${slab.max === Infinity ? 'Above' : '₹' + (slab.max / 100000).toFixed(1) + 'L'}`,
        rate: `${slab.rate}%`,
        amount: taxableInSlab,
        tax: taxForSlab
      });
    }

    totalTax += taxForSlab;
    remainingIncome -= taxableInSlab;
  }

  return {
    baseTax: totalTax,
    slabBreakdown
  };
}

/**
 * Calculate cess (Health & Education Cess)
 * @param {number} tax - Tax amount before cess
 * @returns {number} Cess amount (4%)
 */
export function calculateCess(tax) {
  return tax * 0.04;
}

/**
 * Calculate total tax liability including cess
 * @param {number} taxableIncome - Total taxable income
 * @returns {object} Complete tax calculation
 */
export function calculateTotalTax(taxableIncome) {
  const { baseTax, slabBreakdown } = calculateTaxNewRegime(taxableIncome);
  const cess = calculateCess(baseTax);
  const totalTax = baseTax + cess;

  return {
    taxableIncome,
    baseTax,
    cess,
    totalTax,
    slabBreakdown,
    effectiveRate: taxableIncome > 0 ? (totalTax / taxableIncome) * 100 : 0
  };
}

/**
 * Calculate salary income after standard deduction
 * @param {number} grossSalary - Total salary income
 * @returns {object} Salary income breakdown
 */
export function calculateSalaryIncome(grossSalary) {
  const standardDeduction = Math.min(STANDARD_DEDUCTION, grossSalary);
  const netSalary = grossSalary - standardDeduction;

  return {
    grossSalary,
    standardDeduction,
    netSalary
  };
}

/**
 * Calculate freelance/business income under Section 44ADA
 * (Presumptive taxation - 50% of gross receipts is profit)
 * @param {number} grossReceipts - Total freelance revenue
 * @returns {object} Business income breakdown
 */
export function calculateFreelanceIncome(grossReceipts) {
  const presumptiveProfit = grossReceipts * SECTION_44ADA_PRESUMPTIVE_RATE;
  const deemedExpenses = grossReceipts - presumptiveProfit;

  return {
    grossReceipts,
    presumptiveRate: SECTION_44ADA_PRESUMPTIVE_RATE * 100,
    deemedExpenses,
    taxableProfit: presumptiveProfit
  };
}

/**
 * Calculate complete tax report with both Presumptive (44ADA) and Actual Expense methods
 * Works with user-created categories - treats all income as taxable
 * @param {object} incomeData - Income by category { categoryName: { total, tds } }
 * @param {object} expenseData - Expense by category { categoryName: { total, count } }
 * @returns {object} Complete tax report with both methods
 */
export function calculateTaxReport(incomeData, expenseData = {}) {
  // Aggregate all income and TDS from all categories
  let totalGrossIncome = 0;
  let totalTDS = 0;
  const incomeCategoryBreakdown = [];

  // Process all income categories (user-created)
  Object.entries(incomeData || {}).forEach(([category, data]) => {
    if (data && data.total > 0) {
      totalGrossIncome += data.total;
      totalTDS += data.tds || 0;
      incomeCategoryBreakdown.push({
        name: category,
        gross: data.total,
        tds: data.tds || 0
      });
    }
  });

  // Aggregate all business expenses
  let totalBusinessExpenses = 0;
  const expenseCategoryBreakdown = [];

  // Process all expense categories (user-created)
  Object.entries(expenseData || {}).forEach(([category, data]) => {
    if (data && data.total > 0) {
      totalBusinessExpenses += data.total;
      expenseCategoryBreakdown.push({
        name: category,
        total: data.total,
        count: data.count || 0
      });
    }
  });

  // ===== METHOD 1: Presumptive Taxation (Section 44ADA) =====
  // 50% of gross income is deemed profit, 50% is deemed expense
  const presumptiveDeemedExpenses = totalGrossIncome * SECTION_44ADA_PRESUMPTIVE_RATE;
  const presumptiveTaxableProfit = totalGrossIncome - presumptiveDeemedExpenses;

  // Apply standard deduction for presumptive
  const presumptiveStandardDeduction = Math.min(STANDARD_DEDUCTION, presumptiveTaxableProfit);
  const presumptiveTaxableIncome = Math.max(0, presumptiveTaxableProfit - presumptiveStandardDeduction);

  const presumptiveTaxCalc = calculateTotalTax(presumptiveTaxableIncome);
  const presumptiveNetTaxPayable = Math.max(0, presumptiveTaxCalc.totalTax - totalTDS);
  const presumptiveRefundDue = totalTDS > presumptiveTaxCalc.totalTax ? totalTDS - presumptiveTaxCalc.totalTax : 0;

  // ===== METHOD 2: Actual Expense Method (ITR-3) =====
  // Deduct actual recorded expenses from gross income
  const actualTaxableProfit = Math.max(0, totalGrossIncome - totalBusinessExpenses);

  // Apply standard deduction for actual method
  const actualStandardDeduction = Math.min(STANDARD_DEDUCTION, actualTaxableProfit);
  const actualTaxableIncome = Math.max(0, actualTaxableProfit - actualStandardDeduction);

  const actualTaxCalc = calculateTotalTax(actualTaxableIncome);
  const actualNetTaxPayable = Math.max(0, actualTaxCalc.totalTax - totalTDS);
  const actualRefundDue = totalTDS > actualTaxCalc.totalTax ? totalTDS - actualTaxCalc.totalTax : 0;

  // Determine which method is more beneficial
  const presumptiveBetter = presumptiveTaxCalc.totalTax <= actualTaxCalc.totalTax;
  const taxSavings = Math.abs(presumptiveTaxCalc.totalTax - actualTaxCalc.totalTax);

  // Use the better method as the primary calculation
  const recommendedMethod = presumptiveBetter ? 'presumptive' : 'actual';
  const primaryTaxCalc = presumptiveBetter ? presumptiveTaxCalc : actualTaxCalc;
  const primaryTaxableIncome = presumptiveBetter ? presumptiveTaxableIncome : actualTaxableIncome;
  const primaryNetTaxPayable = presumptiveBetter ? presumptiveNetTaxPayable : actualNetTaxPayable;
  const primaryRefundDue = presumptiveBetter ? presumptiveRefundDue : actualRefundDue;

  return {
    income: {
      gross: totalGrossIncome,
      totalTaxable: primaryTaxableIncome,
      categoryBreakdown: incomeCategoryBreakdown
    },
    expenses: {
      actual: totalBusinessExpenses,
      presumptive: presumptiveDeemedExpenses,
      categoryBreakdown: expenseCategoryBreakdown
    },
    // Presumptive Method (44ADA) calculation
    presumptiveMethod: {
      name: 'Presumptive (Sec 44ADA)',
      description: '50% of gross income is taxable profit',
      itrForm: 'ITR-4',
      grossIncome: totalGrossIncome,
      deemedExpenses: presumptiveDeemedExpenses,
      deemedExpenseRate: '50%',
      taxableProfit: presumptiveTaxableProfit,
      standardDeduction: presumptiveStandardDeduction,
      taxableIncome: presumptiveTaxableIncome,
      tax: presumptiveTaxCalc,
      tdsDeducted: totalTDS,
      netTaxPayable: presumptiveNetTaxPayable,
      refundDue: presumptiveRefundDue
    },
    // Actual Expense Method calculation
    actualMethod: {
      name: 'Actual Expenses (Books)',
      description: 'Actual business expenses deducted from income',
      itrForm: 'ITR-3',
      grossIncome: totalGrossIncome,
      actualExpenses: totalBusinessExpenses,
      taxableProfit: actualTaxableProfit,
      standardDeduction: actualStandardDeduction,
      taxableIncome: actualTaxableIncome,
      tax: actualTaxCalc,
      tdsDeducted: totalTDS,
      netTaxPayable: actualNetTaxPayable,
      refundDue: actualRefundDue
    },
    // Comparison summary
    comparison: {
      recommendedMethod,
      presumptiveBetter,
      taxSavings,
      presumptiveTax: presumptiveTaxCalc.totalTax,
      actualTax: actualTaxCalc.totalTax
    },
    // Use recommended method as primary
    tax: primaryTaxCalc,
    tds: {
      total: totalTDS,
      breakdown: incomeCategoryBreakdown.map(cat => ({
        category: cat.name,
        tds: cat.tds
      }))
    },
    summary: {
      totalTaxLiability: primaryTaxCalc.totalTax,
      totalTDSPaid: totalTDS,
      netTaxPayable: primaryNetTaxPayable,
      refundDue: primaryRefundDue,
      effectiveRate: primaryTaxCalc.effectiveRate,
      recommendedMethod,
      itrForm: presumptiveBetter ? 'ITR-4' : 'ITR-3'
    }
  };
}

/**
 * Calculate advance tax schedule
 * @param {number} totalTax - Total tax liability
 * @param {number} totalTDS - TDS already deducted
 * @returns {array} Advance tax schedule
 */
export function calculateAdvanceTaxSchedule(totalTax, totalTDS) {
  const netTax = Math.max(0, totalTax - totalTDS);

  // Advance tax not required if liability < ₹10,000
  if (netTax < 10000) {
    return {
      required: false,
      message: 'Advance tax not required (liability < ₹10,000)',
      schedule: []
    };
  }

  const schedule = ADVANCE_TAX_DUE_DATES.map((quarter) => {
    const cumulativePercentage = quarter.percentage;
    const cumulativeAmount = (netTax * cumulativePercentage) / 100;

    return {
      quarter: `Q${quarter.quarter}`,
      dueDate: quarter.dueDate,
      cumulativePercentage: `${cumulativePercentage}%`,
      cumulativeAmount,
      installmentAmount:
        quarter.quarter === 1
          ? cumulativeAmount
          : cumulativeAmount - (netTax * ADVANCE_TAX_DUE_DATES[quarter.quarter - 2].percentage) / 100
    };
  });

  return {
    required: true,
    totalAdvanceTax: netTax,
    schedule
  };
}
